// File: frontend/src/App.js
/* eslint-disable import/first */
import React, { Component, useEffect, useMemo, useState, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import toast, { Toaster } from "react-hot-toast";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import PlaceholderPage  from "./pages/PlaceholderPage";
import AdminLayout      from "./layout/AdminLayout";
import { authService }  from "./services/api";
import { getAppTheme }  from "./theme";
import AgentRoute       from "./components/AgentRoute";
import TenantBoundary   from "./components/TenantBoundary";
import { GlobalProvider } from "./context/GlobalContext";
import { AgentProvider }  from "./context/AgentContext";
import { PermissionsProvider } from "./context/PermissionsContext";
import RequirePage from "./components/RequirePage";
import { AnalyticsRedirect, MovedTo, RenamedTo, SalesRoute, SettingsRedirect, ToAmbassadorApp } from "./pages/LegacyRouteRedirects";
import { ColorModeContext } from "./context/ColorModeContext";
import { currentApp } from "./config/origins";
import { consumeAuthFlashMessage } from "./utils/authNotifications";
import { trackFrontendError } from "./utils/telemetry";
import {
  isChunkLoadError,
  attemptChunkRecovery,
  clearRecoveryMarker,
  chunkErrorContext,
} from "./utils/chunkRecovery";

// The public pages (landing, contact, legal, Ambassador programme, not found)
// are declared in publicRoutes.js, because index.js preloads them for the
// static HTML the build writes (scripts/prerender.js).
import {
  LandingPage, ContactPage, Privacy, Terms, PartnersLanding, NotFound,
} from "./publicRoutes";

const Login            = lazy(() => import("./pages/Login"));
// Operator pages carry no webpackPrefetch hint here: App.js is in the main
// bundle, so a hint here would make every public visitor download them. The
// hints live in operatorPagePrefetch.js, loaded by <OperatorPagePrefetch>.
const Dashboard        = lazy(() => import("./pages/Dashboard"));
// The DESTINATIONS, not the panels. Packages and Hotspot Customers each hold
// two datasets now (the catalogue and how it sells; the live console and how
// customers behave) and the destination file owns the title and the tab.
const Packages         = lazy(() => import("./pages/PackagesPage"));
// PROMOTIONS is its own destination since Loyalty Rewards arrived: a promotion
// stopped being a kind of package the moment a reward could be earned by paying
// for one. Not prefetched — it is a page an owner sets up occasionally, not one
// they open every day, and prefetching it would cost every visitor bytes for a
// route most of them never take.
const Promotions       = lazy(() => import("./pages/PromotionsPage"));
const RouterPage       = lazy(() => import("./pages/Routers"));
const BusinessOverview = lazy(() => import("./pages/BusinessOverview"));
// No prefetch on these two. They are destinations an owner goes to on purpose,
// not ones the app should spend a phone's bandwidth on before being asked —
// and Reports in particular pulls in jsPDF for its exports.
const FinancialsHub    = lazy(() => import("./pages/FinancialsHub"));
const Sites            = lazy(() => import("./pages/Sites"));
// Network: the shape of the estate, and the policy that spans it. Both are
// stored-state-only pages (backend/services/networkTopology.js).
const NetworkTopology  = lazy(() => import("./pages/network/Topology"));
// Renamed from Cross-Site Access. Same policy, same endpoint, same safeguards -
// the word people already know. See the file header for the terminology audit.
const NetworkRoaming   = lazy(() => import("./pages/network/Roaming"));
const Vouchers         = lazy(() => import("./pages/Vouchers"));
const HotspotPortal    = lazy(() => import("./pages/HotspotPortal"));
// Sales and Transactions are ONE destination: the same commercial workflow, and
// nobody should have to decide which of two sidebar rows holds their answer.
const SalesAndTransactions = lazy(() => import("./pages/SalesAndTransactions"));
const Wallet           = lazy(() => import("./pages/Wallet"));
const SendMoneyRoute   = lazy(() => import("./pages/wallet/send/SendMoneyRoute"));

// ── PesaFii Ambassadors ────────────────────────────────────────────────────
// The signed-in Ambassador application is a SEPARATE application on
// ambassador.pesafii.net (same bundle, its own tree: see currentApp() below and
// src/ambassador/). This host keeps only the Ambassador surfaces a visitor
// reaches here: the public programme page, referred-business acquisition, and
// an existing person's first business.
const AmbassadorApp    = lazy(() => import("./ambassador/AmbassadorApp"));
const HandoffPage      = lazy(() => import("./auth/HandoffPage"));
const ReferralLanding  = lazy(() => import("./pages/partner/ReferralLanding"));
// The public Ambassadors programme page (PartnersLanding) is lazy for a reason:
// it is the ONLY route that pulls GSAP. See publicRoutes.js.
// An existing person's first business: outside the tenant shell by necessity,
// because the caller has no tenant at the moment they need the page.
const CreateBusiness     = lazy(() => import("./pages/ambassador/CreateBusiness"));
// Float page temporarily hidden from the product (route below is disabled).
// Keep this import commented so the unused component doesn't fail the build;
// restore both this line and the /float route to bring Float back.
// const Float            = lazy(() => import("./pages/Float"));
const Users            = lazy(() => import("./pages/HotspotCustomersPage"));
const Disbursements    = lazy(() => import("./pages/Disbursements"));
const Agents           = lazy(() => import("./pages/Agents"));
const Settings         = lazy(() => import("./pages/Settings"));
const SuperLogin       = lazy(() => import("./pages/SuperLogin"));
const AgentLogin       = lazy(() => import("./pages/AgentLogin"));
const AgentDashboard   = lazy(() => import("./pages/agent/AgentDashboard"));
const AgentVouchers    = lazy(() => import("./pages/agent/AgentVouchers"));
const AgentSell        = lazy(() => import("./pages/agent/AgentSell"));
const AgentCommissions = lazy(() => import("./pages/agent/AgentCommissions"));
const AgentProfile     = lazy(() => import("./pages/agent/AgentProfile"));

// ── §14.3 route splitting: the two SHELLS that are not the tenant admin's ────
// Every PAGE in this app was already lazy; the three layout shells were not, and
// two of the three are unreachable for the customer who pays for the bundle. A
// tenant operator loading /dashboard was downloading the super-admin console's
// chrome (SuperLayout → SuperSidebar → services/superApi) and the sales-agent
// portal's chrome (AgentLayout) inside the main bundle, on every first load, to
// render neither.
//
// AdminLayout stays static above: it is the shell that renders on nearly every
// authenticated route, so deferring it would buy a second round trip before the
// first pixel rather than save anything.
//
// Both are rendered through the same `route()` Suspense wrapper the pages use,
// so a shell chunk that fails to arrive reaches AppErrorBoundary's stale-build
// path exactly as a page chunk does.
const AdministratorLogin   = lazy(() => import("./pages/administrator/AdministratorLogin"));
const AdministratorLayout  = lazy(() => import("./pages/administrator/AdministratorLayout"));
const AdministratorOverview = lazy(() => import("./pages/administrator/AdministratorOverview"));
const AdministratorTenants = lazy(() => import("./pages/administrator/AdministratorTenants"));
const AdministratorAmbassadors = lazy(() => import("./pages/administrator/AdministratorAmbassadors"));
const AdministratorWithdrawals = lazy(() => import("./pages/administrator/AdministratorWithdrawals"));
const AdministratorFloat = lazy(() => import("./pages/administrator/AdministratorFloat"));
const SuperLayout      = lazy(() => import("./components/super/SuperLayout"));
const AgentLayout      = lazy(() => import("./components/AgentLayout"));

// ── Lazy-loaded super admin pages (must come after ALL imports) ───────────────
const SuperStats         = lazy(() => import("./pages/super/SuperStats"));
const SuperTenants       = lazy(() => import("./pages/super/SuperTenants"));
const SuperBusinesses    = lazy(() => import("./pages/super/SuperBusinesses"));
const SuperRevenue       = lazy(() => import("./pages/super/SuperRevenue"));
const SuperAgents        = lazy(() => import("./pages/super/SuperAgents"));
const SuperDisbursements = lazy(() => import("./pages/super/SuperDisbursements"));
const SuperVouchers      = lazy(() => import("./pages/super/SuperVouchers"));
const SuperJobs          = lazy(() => import("./pages/super/SuperJobs"));
const SuperFloat         = lazy(() => import("./pages/super/SuperFloat"));
const SuperWallet        = lazy(() => import("./pages/super/SuperWallet"));
const SuperAudit         = lazy(() => import("./pages/super/SuperAudit"));
const SuperPartners      = lazy(() => import("./pages/super/SuperPartners"));

// ── Color mode context ────────────────────────────────────────────────────────
// Lives in context/ColorModeContext so the Ambassador application tree shares
// this one theme engine. Re-exported for anything that imported it from here.
export { useColorMode } from "./context/ColorModeContext";

function PageLoader() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
      <CircularProgress size={32} sx={{ color: "#3b82f6" }} />
    </Box>
  );
}

class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    // A stale-build failure is not an application bug and must not be presented
    // as one. It gets its own state so the UI can offer an update instead of a
    // crash, and so recovery is bounded rather than automatic-on-everything.
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, info) {
    const chunk = isChunkLoadError(error);

    trackFrontendError(error, {
      source: "react-error-boundary",
      componentStack: info?.componentStack,
      ...(chunk ? chunkErrorContext(error) : {}),
    });

    // ONE automatic reload per build. attemptChunkRecovery returns false when
    // this build has already tried, when storage is unavailable to bound the
    // retry, or when the reload itself fails — in every one of those cases we
    // fall through and show the update screen instead of looping.
    if (chunk) attemptChunkRecovery({ route: window.location.pathname });
  }

  render() {
    if (this.state.hasError) {
      // A stale build is not a crash — the app is fine, this tab is just running
      // code from a deployment that no longer exists. Saying "something went
      // wrong" makes an ordinary release look like a fault and invites support
      // calls, so the two cases get different words and different actions.
      const chunk = this.state.isChunkError;

      return (
        <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 3 }}>
          <Box sx={{ maxWidth: 420, textAlign: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              {chunk ? "A new version of PesaFii is available" : "Something went wrong"}
            </Typography>
            <Typography sx={{ color: "text.secondary", mb: 2 }}>
              {chunk
                ? "This tab is running an older version. Refresh to load the latest one — your work is not affected."
                : "Refresh the page and try again. The error has been logged."}
            </Typography>
            <Button variant="contained" onClick={() => window.location.reload()}>
              {chunk ? "Refresh PesaFii" : "Refresh"}
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

function AuthFlashNotifier() {
  useEffect(() => {
    const message = consumeAuthFlashMessage();
    if (message) toast.error(message, { id: "auth-session-expired" });
  }, []);

  return null;
}

function GlobalErrorReporter() {
  useEffect(() => {
    const handleError = (event) => {
      trackFrontendError(event.error || event.message, {
        source: "window.error",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };
    const handleRejection = (event) => {
      // A failed dynamic import surfaces here as a rejected promise, NOT as a
      // React render error, so the error boundary never sees it. Without this
      // branch a chunk failure during a route transition would be logged and
      // then silently do nothing, leaving the customer on a dead screen.
      const chunk = isChunkLoadError(event.reason);

      trackFrontendError(event.reason, {
        source: "unhandledrejection",
        ...(chunk ? chunkErrorContext(event.reason) : {}),
      });

      if (chunk) attemptChunkRecovery({ route: window.location.pathname });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    // The app rendered, so whatever staleness caused an earlier reload is gone.
    // Releasing the marker lets a LATER deploy in this same tab self-heal too —
    // without it, one recovery would consume the only attempt for the session.
    clearRecoveryMarker();

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  return null;
}

// Loads operatorPagePrefetch.js (and with it the idle-time prefetch hints for
// the daily operator pages) once, for a signed-in visitor or on /login, where
// the next stop is almost always the dashboard. Anonymous visitors to public
// pages never load it.
let operatorPrefetchRequested = false;
function OperatorPagePrefetch() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (operatorPrefetchRequested) return undefined;
    if (pathname !== "/login" && !authService.getCurrentUser()) return undefined;
    const start = () => {
      operatorPrefetchRequested = true;
      // A prefetch hint is optional. If its chunk fails to load, navigation
      // still fetches each page on demand, so the failure is not reported as a
      // stale build (which would reload the page).
      import(/* webpackChunkName: "operator-prefetch" */ "./operatorPagePrefetch").catch(() => {});
    };
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(start, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(start, 1500);
    return () => window.clearTimeout(id);
  }, [pathname]);
  return null;
}

const PrivateRoute = ({ children }) => {
  const user = authService.getCurrentUser();
  return user ? children : <Navigate to="/login" replace />;
};

const SuperPrivateRoute = ({ children }) => {
  const token = localStorage.getItem("super_token");
  return token ? children : <Navigate to="/super-login" replace />;
};

function App() {
  const saved = localStorage.getItem("themeMode");
  const [mode, setMode] = useState(saved || "dark");

  const colorMode = useMemo(() => ({
    mode,
    toggleMode: () => {
      setMode((prev) => {
        const next = prev === "dark" ? "light" : "dark";
        localStorage.setItem("themeMode", next);
        return next;
      });
    },
  }), [mode]);

  const theme = useMemo(() => getAppTheme(mode), [mode]);

  // ── ONE BUNDLE, TWO APPLICATIONS ────────────────────────────────────────────
  // On ambassador.pesafii.net the Ambassador application mounts INSTEAD of the
  // business tree: no GlobalProvider (selected router), no AgentProvider, no
  // PermissionsProvider (tenant grants), no TenantBoundary, no tenant shell. It
  // shares only the theme engine, the error boundary and the toaster above.
  const isAmbassadorHost = currentApp() === "ambassador";

  // AdminLayout renders the sidebar/shell; RequirePage gates the page content
  // (shows AccessDenied inside the shell for pages the user can't open). The
  // backend independently enforces every API call regardless of this gate.
  // TenantBoundary sits INSIDE PrivateRoute and OUTSIDE the shell: no tenant
  // page renders until the current account's identity and permissions have
  // resolved, and everything below it is keyed by tenantId:userId so page state
  // cannot survive an account change.
  const withShell = (node) => (
    <PrivateRoute>
      <TenantBoundary>
        <AdminLayout>
          <RequirePage>{node}</RequirePage>
        </AdminLayout>
      </TenantBoundary>
    </PrivateRoute>
  );

  const route = (node) => <Suspense fallback={<PageLoader />}>{node}</Suspense>;

  return (
    <AppErrorBoundary>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Toaster
            position="top-right"
            toastOptions={{ duration: 2500, style: { borderRadius: 14, padding: "10px 12px" } }}
          />
          <AuthFlashNotifier />
          <GlobalErrorReporter />
          {isAmbassadorHost ? (
            <Suspense fallback={<PageLoader />}><AmbassadorApp /></Suspense>
          ) : (
          <GlobalProvider>
            <AgentProvider>
              <PermissionsProvider>
              <Router>
                <OperatorPagePrefetch />
                <Routes>

                {/* ── Tenant Admin ─────────────────────────────────────── */}
                <Route path="/login"         element={route(<Login />)} />
                {/* Beta uses username/password staff creation — the old email
                    invite-accept flow is retired. /accept-invite now redirects. */}
                <Route path="/accept-invite" element={<RenamedTo path="/login" />} />
                <Route path="/hotspot"       element={route(<HotspotPortal />)} />
                {/* Public landing page. Previously "/" redirected to /dashboard;
                    it now serves the public marketing page. Authenticated users
                    see an "Open Dashboard" CTA (the header detects auth state).
                    Protected routes below are unchanged. */}
                <Route path="/"              element={route(<LandingPage />)} />
                <Route path="/contact"       element={route(<ContactPage />)} />
                {/* Public legal pages — readable without an account (also linked
                    from the captive portal, whose walled garden already allows
                    pesafii.net pre-auth). */}
                {/* Public referral landing. No authentication: it shows a display
                    name and hands the browser an opaque, purpose-bound ticket.
                    An unknown code and a suspended Partner look identical. */}
                <Route path="/r/:code"       element={route(<ReferralLanding />)} />
                {/* Public PesaFii Ambassadors programme page, canonical at
                    /ambassador (singular). Readable without an account and NEVER
                    redirected away because someone is signed in: it is the case
                    FOR joining. The signed-in workspace is ambassador.pesafii.net. */}
                <Route path="/ambassador"    element={route(<PartnersLanding />)} />
                <Route path="/ambassadors"   element={<RenamedTo path="/ambassador" />} />
                {/* The receiving end of a workspace switch from the Ambassador
                    application (one-time code in the URL fragment). */}
                <Route path="/auth/handoff"  element={route(<HandoffPage loginPath="/login" />)} />
                <Route path="/privacy"       element={route(<Privacy />)} />
                <Route path="/terms"         element={route(<Terms />)} />
                <Route path="/dashboard"     element={withShell(route(<Dashboard />))} />
                <Route path="/business"      element={withShell(route(<BusinessOverview />))} />
                {/* ONE Financials destination. Money, Sales and Reports are
                    its tabs; the three engines underneath are unchanged. */}
                <Route path="/financials"    element={withShell(route(<FinancialsHub />))} />
                {/* /reports was a main sidebar item and is in bookmarks, and
                    Reports' own empty states link to it. It now opens the hub
                    ON the Reports tab rather than dropping the reader on Money. */}
                <Route path="/reports"       element={<MovedTo path="/financials" tab="reports" />} />
                <Route path="/sites"         element={withShell(route(<Sites />))} />
                {/* Network pages sit under /network so canPage() normalises them
                    to one permission base — see backend/config/permissions.js,
                    where /network maps onto the existing routers.view key. */}
                <Route path="/network/topology"          element={withShell(route(<NetworkTopology />))} />
                <Route path="/network/roaming"           element={withShell(route(<NetworkRoaming />))} />
                {/* ROUTER, singular: the station chosen in the sidebar. It also
                    holds what used to be two separate destinations - Remote
                    Access and the captive portal editor - as tabs. */}
                <Route path="/router"        element={withShell(route(<RouterPage />))} />
                <Route path="/packages"      element={withShell(route(<Packages />))} />
                {/* Promotions sits beside Packages, not inside it. canPage()
                    resolves it through PAGE_PERMISSION["/promotions"], which
                    reuses packages.view — so moving promotion management out of
                    Packages changes WHERE it is, never WHO can reach it. */}
                <Route path="/promotions"    element={withShell(route(<Promotions />))} />
                <Route path="/vouchers"      element={withShell(route(<Vouchers />))} />
                {/* /sales: only ?tab=payments still renders this page; every
                    other /sales link lands in the matching Financials tab. */}
                <Route path="/sales"         element={<SalesRoute>{withShell(route(<SalesAndTransactions />))}</SalesRoute>} />
                {/* Float is temporarily hidden from the product (per business: it
                    confuses clients). Route disabled so /float is unreachable;
                    the Float page + backend stay intact for a future launch. */}
                {/* <Route path="/float"         element={withShell(route(<Float />))} /> */}
                <Route path="/users"         element={withShell(route(<Users />))} />
                <Route path="/disbursements" element={withShell(route(<Disbursements />))} />
                <Route path="/agents"        element={withShell(route(<Agents />))} />
                {/* Settings is three destinations under ONE permission base -
                    canPage normalises "/settings/x" to "/settings" - so nobody
                    gains or loses access in the split. Bare /settings lands on
                    Team & Permissions, which is what the single row opened. */}
                {/* An old ?tab= lands on the section it names (§26). */}
                <Route path="/settings"                  element={<SettingsRedirect />} />
                <Route path="/settings/team"             element={withShell(route(<Settings section="team" />))} />
                <Route path="/settings/wallet-security"  element={withShell(route(<Settings section="wallet-security" />))} />
                <Route path="/settings/activity"         element={withShell(route(<Settings section="activity" />))} />
                <Route path="/wallet"        element={withShell(route(<Wallet />))} />
                {/* Send Money is a ROUTE, not only a dialog, so a refresh during
                    a transaction has somewhere to land. /status/:reference is
                    declared FIRST because it would otherwise be swallowed by
                    the :step pattern below it. See pages/wallet/send. */}
                {/* ONE route for every step. Three separate Route entries
                    remounted the component on each navigation, which threw away
                    the in-flight transaction — including the reason a payout
                    was refused. The splat keeps one instance for the journey. */}
                <Route path="/wallet/send/*" element={withShell(route(<SendMoneyRoute />))} />
                {/* -- Retired routes -----------------------------------------
                    Bookmarks, emailed links and every insight card the backend
                    has emitted still point at these. Each carries its query
                    string through and translates the tab, so a drill-down lands
                    on the analysis it promised rather than on a page default -
                    which would be worse than a 404, because it looks like an
                    answer. See pages/LegacyRouteRedirects.js. */}
                <Route path="/analytics"     element={<AnalyticsRedirect />} />
                <Route path="/transactions"  element={<MovedTo path="/financials" tab="transactions" />} />
                <Route path="/remote-access" element={<MovedTo path="/router" tab="remote" />} />
                <Route path="/routers"       element={<MovedTo path="/router" />} />
                <Route path="/network/cross-site-access" element={<MovedTo path="/network/roaming" />} />
                {/* /sales/agents was a placeholder for a page that shipped as
                    /agents; it is a redirect now, not a second stub. */}
                <Route path="/sales/agents"  element={<RenamedTo path="/agents" />} />
                <Route path="/support"       element={withShell(<PlaceholderPage title="Support"       note="Support will appear here." />)} />

                {/* ── Super Admin — login ──────────────────────────────── */}
                <Route path="/super-login" element={route(<SuperLogin />)} />

                {/* Administrator console: its own sign-in, checked by the server on every request. */}
                <Route path="/administrator/login" element={route(<AdministratorLogin />)} />
                <Route path="/administrator" element={route(<AdministratorLayout />)}>
                  <Route index element={route(<AdministratorOverview />)} />
                  <Route path="tenants" element={route(<AdministratorTenants />)} />
                  <Route path="ambassadors" element={route(<AdministratorAmbassadors />)} />
                  <Route path="float" element={route(<AdministratorFloat />)} />
                  <Route path="withdrawals" element={route(<AdministratorWithdrawals />)} />
                </Route>

                {/* ── Super Admin — the multi-page console (/super and /super/*)
                    There used to be a second <Route path="/super"> declared
                    ABOVE this one, rendering the old single-page SuperDashboard.
                    React Router matches the first literal match, so bare /super
                    served the legacy console purely by declaration order, while
                    every in-app link (SuperSidebar, SuperLogin) pointed at
                    /super/stats. The new console is a strict superset of the
                    old one — it carries all ten of its sections plus Wallet and
                    Partners — and seven of the legacy console's ten sections
                    were never wired to a service at all, so nothing is lost by
                    letting the index redirect below own bare /super. */}
                <Route
                  path="/super"
                  element={<SuperPrivateRoute>{route(<SuperLayout />)}</SuperPrivateRoute>}
                >
                  <Route index                element={<Navigate to="stats" replace />} />
                  <Route path="stats"         element={route(<SuperStats />)} />
                  <Route path="tenants"       element={route(<SuperTenants />)} />
                  <Route path="businesses"    element={route(<SuperBusinesses />)} />
                  <Route path="revenue"       element={route(<SuperRevenue />)} />
                  <Route path="agents"        element={route(<SuperAgents />)} />
                  <Route path="disbursements" element={route(<SuperDisbursements />)} />
                  <Route path="vouchers"      element={route(<SuperVouchers />)} />
                  <Route path="jobs"          element={route(<SuperJobs />)} />
                  <Route path="wallet"        element={route(<SuperWallet />)} />
                  <Route path="float"         element={route(<SuperFloat />)} />
                  <Route path="partners"      element={route(<SuperPartners />)} />
                  <Route path="audit"         element={route(<SuperAudit />)} />
                  <Route path="*"             element={<Navigate to="stats" replace />} />
                </Route>

                {/* ── Agent Portal ─────────────────────────────────────── */}
                {/* ── PesaFii Ambassadors: old signed-in URLs ────────────────
                    The workspace moved to ambassador.pesafii.net. Every nested
                    /ambassador/* path (clients, earnings, program, join,
                    register, …) moves there with its query and hash, signed in
                    via the one-time handoff when the person is signed in here.
                    Bare /ambassador is NOT matched here: the exact route above
                    outranks this splat, so the public page never redirects.
                    `partner_*` stays the internal engineering namespace. */}
                <Route path="/ambassador/*" element={<ToAmbassadorApp />} />

                {/* An existing person's FIRST business. Not inside withShell:
                    the whole point is that they do not have a tenant yet, so
                    TenantBoundary would bounce them straight back out. */}
                <Route path="/business/create" element={<PrivateRoute>{route(<CreateBusiness />)}</PrivateRoute>} />

                {/* ── Compatibility ───────────────────────────────────────
                    Old links, bookmarks and anything already shared. Listed
                    explicitly rather than as a wildcard so every mapping is
                    visible and nothing is redirected by accident. `replace` so
                    Back does not land on the redirect again. */}
                <Route path="/partners"         element={<RenamedTo path="/ambassador" />} />
                <Route path="/partner"          element={<ToAmbassadorApp />} />
                <Route path="/partner/*"        element={<ToAmbassadorApp />} />

                <Route path="/agent/login" element={route(<AgentLogin />)} />
                {/* RenamedTo, not a bare Navigate: a link into the portal keeps
                    its query (§26 — every redirect carries its query through). */}
                <Route path="/agent"       element={<RenamedTo path="/agent/dashboard" />} />
                <Route path="/agent" element={<AgentRoute>{route(<AgentLayout />)}</AgentRoute>}>
                  <Route path="dashboard"   element={route(<AgentDashboard />)} />
                  <Route path="vouchers"    element={route(<AgentVouchers />)} />
                  <Route path="sell"        element={route(<AgentSell />)} />
                  <Route path="commissions" element={route(<AgentCommissions />)} />
                  <Route path="profile"     element={route(<AgentProfile />)} />
                </Route>

                {/* ── Fallback ─────────────────────────────────────────── */}
                {/* An unknown path is a 404, not a detour through /dashboard to
                    /login. On pesafii.net the server already answered it with
                    HTTP 404 (vercel.json + build/404.html); this renders the
                    same page for in-app navigation. */}
                <Route path="*" element={route(<NotFound />)} />

                </Routes>
              </Router>
              </PermissionsProvider>
            </AgentProvider>
          </GlobalProvider>
          )}
        </ThemeProvider>
      </ColorModeContext.Provider>
    </AppErrorBoundary>
  );
}

export default App;
